/**
=========================================================
* Trust Cargo - loading indicator
=========================================================

* A shipping container travelling under a customs seal, instead of a generic
* spinner. It reads as "your shipment is being processed" rather than "the
* browser is busy", which is what people on these screens are actually
* waiting for.
*
* Pure SVG + CSS keyframes - no library, no image request, so it appears
* instantly even on the first paint of a slow page.
*/

import PropTypes from "prop-types";
import { keyframes } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

// the seal rotates slowly, like a stamp being lined up
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// containers slide along the belt and fade at each end
const travel = keyframes`
  0%   { transform: translateX(-14px); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateX(14px); opacity: 0; }
`;

// the seal breathes so it never looks frozen if the rotation is subtle
const pulse = keyframes`
  0%, 100% { opacity: 0.85; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.04); }
`;

function CargoLoader({ label, size }) {
  return (
    <SuiBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1.5}
      py={4}
      width="100%"
    >
      <SuiBox
        position="relative"
        display="grid"
        sx={{ placeItems: "center", width: size, height: size }}
      >
        {/* customs seal - dashed gold ring */}
        <SuiBox
          component="svg"
          viewBox="0 0 100 100"
          sx={({ palette }) => ({
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            animation: `${rotate} 6s linear infinite`,
            color: palette.primary.main,
          })}
        >
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="6 9"
            strokeLinecap="round"
            opacity="0.75"
          />
          <circle
            cx="50"
            cy="50"
            r="37"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.35"
          />
        </SuiBox>

        {/* the container on its belt */}
        <SuiBox
          component="svg"
          viewBox="0 0 64 64"
          sx={({ palette }) => ({
            width: "56%",
            height: "56%",
            color: palette.dark.main,
            animation: `${pulse} 2.4s ease-in-out infinite`,
          })}
        >
          {/* container body */}
          <rect x="14" y="22" width="36" height="22" rx="2.5" fill="currentColor" opacity="0.92" />
          {/* corrugation */}
          <g stroke="#F7F4EE" strokeWidth="1.6" opacity="0.5">
            <line x1="21" y1="25" x2="21" y2="41" />
            <line x1="27" y1="25" x2="27" y2="41" />
            <line x1="33" y1="25" x2="33" y2="41" />
            <line x1="39" y1="25" x2="39" y2="41" />
            <line x1="45" y1="25" x2="45" y2="41" />
          </g>
          {/* belt */}
          <line
            x1="8"
            y1="50"
            x2="56"
            y2="50"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.28"
          />
          {/* travelling crates on the belt */}
          <g fill="#C9A227">
            <rect x="16" y="45" width="5" height="4" rx="1">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-10 0; 34 0"
                dur="2.2s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="16" y="45" width="5" height="4" rx="1" opacity="0.6">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-24 0; 20 0"
                dur="2.2s"
                repeatCount="indefinite"
              />
            </rect>
          </g>
        </SuiBox>
      </SuiBox>

      {label ? (
        <SuiBox display="flex" alignItems="center" gap={0.75}>
          <SuiTypography
            variant="button"
            fontWeight="medium"
            sx={({ palette, functions: { rgba } }) => ({
              color: rgba(palette.dark.main, 0.66),
              letterSpacing: "0.06em",
            })}
          >
            {label}
          </SuiTypography>
          {/* three dots, staggered, so the label itself looks alive */}
          {[0, 1, 2].map((i) => (
            <SuiBox
              key={i}
              sx={({ palette }) => ({
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: palette.primary.main,
                animation: `${travel} 1.4s ease-in-out ${i * 0.18}s infinite`,
              })}
            />
          ))}
        </SuiBox>
      ) : null}
    </SuiBox>
  );
}

CargoLoader.defaultProps = {
  label: "Loading",
  size: 92,
};

CargoLoader.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  size: PropTypes.number,
};

export default CargoLoader;
